import React, { useState, useRef, useEffect } from 'react';
import { postService } from '../services/postService';
import AvatarDisplay from './AvatarDisplay';

const ComposeTweet = ({ 
  onPost, 
  isLoading, 
  currentUserENS, 
  currentUserAddress,
  replyToPostId = null,
  replyToAuthor = '',
  onCancelReply = null
}) => {
  const [content, setContent] = useState('');
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  const MAX_CHARS = 280;
  const ALLOWED_FILE_TYPES = [
    'image/png', 'image/jpeg', 'image/gif', 'image/webp',
    'video/mp4', 'video/webm', 'video/ogg', 'video/quicktime',
    'application/pdf', 'text/plain', 'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ];
  const MAX_FILE_SIZE = 25 * 1024 * 1024;

  // Handle reply mode - UPDATED: No @ symbol prefix
  useEffect(() => {
    if (replyToAuthor && textareaRef.current) {
      // Just focus the textarea, don't pre-fill with @ symbol
      textareaRef.current.focus();
      textareaRef.current.style.height = 'auto';
      const newHeight = Math.min(textareaRef.current.scrollHeight, 200);
      textareaRef.current.style.height = `${newHeight}px`;
      
      // Optional: You can add a mention if you want, but clean version:
      // setContent(`@${replyToAuthor} `);
      // For clean version, just leave empty and focus
    }
  }, [replyToAuthor]);

  // Handle file selection
  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    if (selectedFile.size > MAX_FILE_SIZE) {
      setError('File too large. Maximum size is 10MB.');
      return;
    }

    if (!ALLOWED_FILE_TYPES.includes(selectedFile.type)) {
      setError('Invalid file type. Allowed: PNG, JPG, GIF, WEBP, MP4, WEBM, OGG, MOV, PDF, TXT, DOC, DOCX');
      return;
    }

    setFile(selectedFile);
    setError(null);

    if (selectedFile.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setFilePreview(e.target.result);
      reader.readAsDataURL(selectedFile);
    } else {
      setFilePreview(null);
    }
  };

  // Remove file
  const removeFile = () => {
    setFile(null);
    setFilePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Upload file to backend
  const uploadFile = async () => {
    if (!file) return null;

    setUploading(true);
    try {
      const result = await postService.uploadFile(file);
      console.log('✅ File uploaded:', result);
      console.log('📦 uploadFile result (full):', JSON.stringify(result, null, 2));
      return result.file; // return the full file object
    } catch (error) {
      console.error('Failed to upload file:', error);
      setError(`Failed to upload file: ${error.message}`);
      return null;
    } finally {
      setUploading(false);
    }
  };

  // Handle submit - UPDATED: Pass replyToPostId and full file object
  const handleSubmit = async () => {
    if (!content.trim() && !file) {
      setError('Please enter some text or attach a file');
      return;
    }

    if (content.length > MAX_CHARS) {
      setError(`Post is too long. Maximum ${MAX_CHARS} characters.`);
      return;
    }

    let fileObject = null;
    if (file) {
      fileObject = await uploadFile();
      if (!fileObject) return; // upload failed
    }

    // Pass fileObject (contains id, filename, type, size, url) to parent
    onPost(content, fileObject, replyToPostId);

    // Reset form
    resetForm();

    // If this was a reply, notify parent to clear reply state
    if (replyToPostId && onCancelReply) {
      onCancelReply();
    }
  };

  // Reset form
  const resetForm = () => {
    setContent('');
    setFile(null);
    setFilePreview(null);
    setError(null);
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Auto-resize textarea
  const handleTextareaChange = (e) => {
    setContent(e.target.value);
    
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const newHeight = Math.min(textareaRef.current.scrollHeight, 200);
      textareaRef.current.style.height = `${newHeight}px`;
    }
  };

  // Handle key events
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSubmit();
    }
  };

  // Character counter
  const charsLeft = MAX_CHARS - content.length;
  const isOverLimit = charsLeft < 0;
  const canSubmit = !isLoading && !uploading && !isOverLimit && (content.trim() || file);

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="zpost-compose-tweet">
      {/* Reply Header - Clean design */}
      {replyToAuthor && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0.75rem 1rem',
          background: 'rgba(255, 215, 0, 0.05)',
          border: '1px solid var(--gold)',
          borderRadius: '8px 8px 0 0',
          marginBottom: '0.5rem',
          marginTop: '-1rem', // Compensate for parent padding
          marginLeft: '-1rem',
          marginRight: '-1rem'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem',
            color: 'var(--text-light)',
            fontSize: '0.9rem'
          }}>
            <span style={{ 
              color: 'var(--gold)',
              fontSize: '1rem',
              fontWeight: 'bold'
            }}>
              ↩
            </span>
            <span>
              Replying to <strong style={{ color: 'var(--gold)' }}>{replyToAuthor}</strong>
            </span>
          </div>
          {onCancelReply && (
            <button
              onClick={onCancelReply}
              style={{
                background: 'rgba(255, 215, 0, 0.1)',
                border: '1px solid var(--border)',
                color: 'var(--text-secondary)',
                padding: '0.25rem 0.75rem',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.8rem',
                fontWeight: 'bold',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 215, 0, 0.2)';
                e.currentTarget.style.borderColor = 'var(--gold)';
                e.currentTarget.style.color = 'var(--text-light)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 215, 0, 0.1)';
                e.currentTarget.style.borderColor = 'var(--border)';
                e.currentTarget.style.color = 'var(--text-secondary)';
              }}
            >
              Cancel
            </button>
          )}
        </div>
      )}

      <div className="compose-container">
        {/* User Avatar */}
        <div className="compose-avatar">
          <AvatarDisplay
            ensName={currentUserENS}
            address={currentUserAddress}
            size={48}
          />
        </div>

        {/* Compose Area */}
        <div className="compose-content">
          {/* Textarea */}
          <textarea
            ref={textareaRef}
            placeholder={replyToAuthor ? `Reply to ${replyToAuthor}` : "What's happening?"}
            value={content}
            onChange={handleTextareaChange}
            onKeyDown={handleKeyDown}
            disabled={isLoading || uploading}
            className="compose-textarea"
            maxLength={MAX_CHARS}
          />

          {/* File Preview (Image) */}
          {filePreview && (
            <div className="file-preview image-preview">
              <div className="preview-header">
                <span className="file-name">{file.name}</span>
                <button
                  onClick={removeFile}
                  className="remove-file-button"
                  aria-label="Remove file"
                >
                  ✕
                </button>
              </div>
              <img
                src={filePreview}
                alt="Preview"
                className="preview-image"
                onError={(e) => {
                  e.target.style.display = 'none';
                  console.warn(`Image preview failed: ${file.name}`);
                }}
              />
            </div>
          )}

          {/* File Info (Non-image files) */}
          {file && !filePreview && (
            <div className="file-preview document-preview">
              <div className="file-icon">📄</div>
              <div className="file-details">
                <div className="file-name">{file.name}</div>
                <div className="file-meta">
                  {file.type.split('/')[1]?.toUpperCase()} • {formatFileSize(file.size)}
                </div>
              </div>
              <button
                onClick={removeFile}
                className="remove-file-button"
                aria-label="Remove file"
              >
                ✕
              </button>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="compose-error">
              <span className="error-text">{error}</span>
              <button
                onClick={() => setError(null)}
                className="dismiss-error"
                aria-label="Dismiss error"
              >
                ×
              </button>
            </div>
          )}

          {/* Bottom Bar */}
          <div className="compose-actions">
            {/* Left: Actions */}
            <div className="action-buttons">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                accept={ALLOWED_FILE_TYPES.map(type => `.${type.split('/')[1]}`).join(',')}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isLoading || uploading}
                className="action-button attach-button"
                title="Attach file"
                aria-label="Attach file"
              >
                📎
              </button>
            </div>

            {/* Right: Character count and Submit */}
            <div className="submit-section">
              <div className={`char-counter ${isOverLimit ? 'over-limit' : charsLeft < 20 ? 'warning' : ''}`}>
                {charsLeft}
              </div>
              
              <button
                onClick={handleSubmit}
                disabled={!canSubmit}
                className={`submit-button ${!canSubmit ? 'disabled' : ''}`}
              >
                {uploading ? (
                  <>
                    <span className="spinner"></span>
                    Uploading...
                  </>
                ) : isLoading ? (
                  <>
                    <span className="spinner"></span>
                    Posting...
                  </>
                ) : replyToAuthor ? (
                  'Reply'
                ) : (
                  'Post'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Inline styles for this component */}
      <style jsx>{`
        .zpost-compose-tweet {
          background: var(--card-bg);
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 1rem;
          margin-bottom: 1rem;
          position: relative;
        }

        .compose-container {
          display: flex;
          gap: 0.75rem;
        }

        .compose-avatar {
          flex-shrink: 0;
        }

        .compose-content {
          flex: 1;
          min-width: 0;
        }

        .compose-textarea {
          width: 100%;
          min-height: 60px;
          max-height: 200px;
          background: transparent;
          border: none;
          color: var(--text-light);
          font-size: 1rem;
          font-family: Helvetica, Arial, sans-serif;
          resize: none;
          outline: none;
          line-height: 1.4;
          margin-bottom: 0.5rem;
          padding: 0;
        }

        .compose-textarea:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .compose-textarea::placeholder {
          color: var(--text-secondary);
          opacity: 0.7;
        }

        .file-preview {
          margin-bottom: 1rem;
          border: 1px solid var(--border);
          border-radius: 8px;
          overflow: hidden;
        }

        .image-preview {
          max-width: 300px;
        }

        .preview-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0.5rem;
          background: rgba(0, 0, 0, 0.3);
          border-bottom: 1px solid var(--border);
        }

        .file-name {
          font-size: 0.8rem;
          color: var(--text-secondary);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 200px;
        }

        .remove-file-button {
          background: transparent;
          border: none;
          color: #ff4444;
          cursor: pointer;
          font-size: 1rem;
          padding: 0.25rem;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
        }

        .remove-file-button:hover {
          background: rgba(255, 68, 68, 0.1);
        }

        .preview-image {
          width: 100%;
          height: auto;
          display: block;
          max-height: 300px;
          object-fit: contain;
        }

        .document-preview {
          padding: 0.75rem;
          background: rgba(255, 215, 0, 0.05);
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .file-icon {
          font-size: 1.5rem;
          min-width: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .file-details {
          flex: 1;
          min-width: 0;
          overflow: hidden;
        }

        .file-meta {
          color: var(--text-secondary);
          font-size: 0.8rem;
          margin-top: 0.25rem;
        }

        .compose-error {
          color: #ff4444;
          font-size: 0.85rem;
          margin-bottom: 0.75rem;
          padding: 0.5rem;
          background: rgba(255, 68, 68, 0.1);
          border-radius: 6px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .error-text {
          flex: 1;
        }

        .dismiss-error {
          background: none;
          border: none;
          color: #ff4444;
          cursor: pointer;
          font-size: 1.2rem;
          margin-left: 0.5rem;
          padding: 0.25rem;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
        }

        .dismiss-error:hover {
          background: rgba(255, 68, 68, 0.1);
        }

        .compose-actions {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding-top: 0.75rem;
          border-top: 1px solid var(--border);
        }

        .action-buttons {
          display: flex;
          gap: 0.5rem;
        }

        .action-button {
          background: transparent;
          border: none;
          color: var(--gold);
          cursor: pointer;
          font-size: 1.25rem;
          padding: 0.5rem;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          transition: all 0.2s ease;
        }

        .action-button:hover:not(:disabled) {
          background: rgba(255, 215, 0, 0.1);
          transform: scale(1.05);
        }

        .action-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .submit-section {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .char-counter {
          font-size: 0.9rem;
          min-width: 30px;
          text-align: center;
        }

        .char-counter.warning {
          color: var(--gold);
        }

        .char-counter.over-limit {
          color: #ff4444;
          font-weight: bold;
        }

        .submit-button {
          background: var(--gold);
          color: var(--black);
          border: none;
          padding: 0.5rem 1.5rem;
          border-radius: 9999px;
          font-weight: bold;
          cursor: pointer;
          font-size: 0.9rem;
          min-width: 80px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          transition: all 0.2s ease;
        }

        .submit-button:hover:not(.disabled) {
          background: #FFE44D;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px var(--gold-shadow);
        }

        .submit-button.disabled {
          background: #666;
          color: var(--text-secondary);
          cursor: not-allowed;
          opacity: 0.7;
        }

        .spinner {
          display: inline-block;
          width: 12px;
          height: 12px;
          border: 2px solid rgba(0, 0, 0, 0.3);
          border-top: 2px solid var(--black);
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ComposeTweet;
